import {
  MatchUpFragment,
  MatchUpTeamParticipantFragment,
  MatchUpIndividualParticipantFragment,
  MatchUpPhaseFragment,
  PlayableMatchUpMatchFragment,
  UpcomingKingOfTheHillParticipantFragment,
} from 'src/generated/graphql';
import { ScheduleTableData } from './ScheduleTab';
import { isTypename, isTypenameCallback } from 'src/utilities/TSUtilities';
import {
  getIndividualParticipantsFromParticipantReference,
  getTeamParticipantsFromParticipantReference,
} from '@pages/Events/EventUtilities';
import { isFilled } from 'ts-is-present';
import { FAKE_PHASE_ID } from '../../Competitions/MatchUps/MatchUpDetail/MatchUpDetailHelpers';
import { DateTime } from 'luxon';

const getHostFromMatch = (
  match: PlayableMatchUpMatchFragment,
  host: MatchUpTeamParticipantFragment | MatchUpIndividualParticipantFragment
) =>
  match.participants
    .flatMap((participant) => participant.participant)
    .find(
      (participant) =>
        (isTypename('TeamParticipant', participant) ||
          isTypename('IndividualParticipant', participant)) &&
        participant.id === host.id
    );

const getParticipantFromMatch = (
  match: PlayableMatchUpMatchFragment,
  host: MatchUpTeamParticipantFragment | MatchUpIndividualParticipantFragment
) =>
  match.participants
    .flatMap((participant) => participant.participant)
    .find(
      (participant) =>
        (isTypename('TeamParticipant', participant) ||
          isTypename('IndividualParticipant', participant)) &&
        participant.id !== host.id
    );

export const getInProgressMatchWithParticipants = (
  matchUp: MatchUpFragment,
  host: MatchUpTeamParticipantFragment | MatchUpIndividualParticipantFragment
) => {
  const incompletePhases = matchUp.phases
    // Don't want completed phases.
    .filter((phase) => !phase.isComplete)
    // Sort by phase number and select the lowest one (current phase)
    .sort((a, b) => a.phaseNumber - b.phaseNumber);

  // if there is an incomplete phase with matches, get the last one.
  const match =
    incompletePhases.length > 0 && incompletePhases[0].matches.length > 0
      ? incompletePhases[0].matches.find(
          isTypenameCallback('PlayableMatchUpMatch')
        )
      : undefined;

  const challenger = match && getParticipantFromMatch(match, host);
  const hostFromMatch = match && getHostFromMatch(match, host);

  return match &&
    challenger &&
    hostFromMatch &&
    (isTypename('IndividualParticipant', challenger) ||
      isTypename('TeamParticipant', challenger)) &&
    (isTypename('IndividualParticipant', hostFromMatch) ||
      isTypename('TeamParticipant', hostFromMatch))
    ? {
        match: match,
        challenger: challenger,
        host: hostFromMatch,
      }
    : undefined;
};

export const getPreviousMatchWithParticipants = (
  matchUp: MatchUpFragment,
  host: MatchUpTeamParticipantFragment | MatchUpIndividualParticipantFragment
) => {
  const completePhases = matchUp.phases
    // Only want completed phases.
    .filter((phase) => phase.isComplete)
    // Sort by phase number and select the highest one (previous phase)
    .sort((a, b) => b.phaseNumber - a.phaseNumber);

  // if there is a complete phase with matches, get the last one.
  const match =
    completePhases.length > 0
      ? completePhases[0].matches.find(
          isTypenameCallback('PlayableMatchUpMatch')
        )
      : undefined;

  const challenger = match && getParticipantFromMatch(match, host);
  const hostFromMatch = match && getHostFromMatch(match, host);

  return match &&
    challenger &&
    hostFromMatch &&
    (isTypename('IndividualParticipant', challenger) ||
      isTypename('TeamParticipant', challenger)) &&
    (isTypename('IndividualParticipant', hostFromMatch) ||
      isTypename('TeamParticipant', hostFromMatch))
    ? {
        match: match,
        challenger: challenger,
        host: hostFromMatch,
      }
    : undefined;
};

export const getNextUpcomingKOTHParticipant = (matchUp: MatchUpFragment) => {
  const remainingParticipants =
    matchUp.matchUpFormatSpecificDetails &&
    isTypename(
      'KingOfTheHillSpecificDetails',
      matchUp.matchUpFormatSpecificDetails
    )
      ? matchUp.matchUpFormatSpecificDetails.remainingParticipants
      : [];

  return remainingParticipants.length > 0
    ? remainingParticipants.slice().sort((a, b) => {
        if (a.order === b.order) {
          return 0;
        }

        if (a.order === null) {
          return 1;
        }

        if (b.order === null) {
          return -1;
        }

        return a.order - b.order;
      })[0]
    : undefined;
};

export const getUpcomingMatchWithParticipants = (
  matchUp: MatchUpFragment,
  host: MatchUpTeamParticipantFragment | MatchUpIndividualParticipantFragment
) => {
  const match = matchUp.phases.find((phase) => phase.id === FAKE_PHASE_ID)
    ?.matches[0];

  if (!match || !isTypename('PlayableMatchUpMatch', match)) {
    return null;
  }

  const challenger = getParticipantFromMatch(match, host);
  const hostFromMatch = getHostFromMatch(match, host);

  return challenger &&
    hostFromMatch &&
    (isTypename('IndividualParticipant', challenger) ||
      isTypename('TeamParticipant', challenger)) &&
    (isTypename('IndividualParticipant', hostFromMatch) ||
      isTypename('TeamParticipant', hostFromMatch))
    ? {
        match: match,
        challenger: challenger,
        host: hostFromMatch,
      }
    : undefined;
};

export const getChallengerAndHostScoresFromMatch = (
  match: PlayableMatchUpMatchFragment,
  participantId: string,
  hostId: string
) =>
  match.rounds.reduce(
    (acc, round) => {
      const roundParticipants = [
        ...getTeamParticipantsFromParticipantReference(round.participants),
        ...getIndividualParticipantsFromParticipantReference(
          round.participants
        ),
      ];

      const challengerRoundParticipant = roundParticipants.find(
        (roundParticipant) => roundParticipant.id === participantId
      );

      const hostRoundParticipant = roundParticipants.find(
        (roundParticipant) => roundParticipant.id === hostId
      );

      if (challengerRoundParticipant?.result?.placement === 1) {
        acc.challengerWins += 1;
      }

      if (hostRoundParticipant?.result?.placement === 1) {
        acc.hostWins += 1;
      }

      return acc;
    },
    { challengerWins: 0, hostWins: 0 }
  );
export type ResultText = {
  tba: string;
  challengerWon: string;
  hostWon: string;
  tie: string;
};

export const getResultText = (
  match: PlayableMatchUpMatchFragment,
  results: { challengerWins: number; hostWins: number },
  resultText: ResultText
) => {
  const { challengerWins, hostWins } = results;

  let resultDisplay = resultText.tba;

  if (match.isComplete) {
    if (challengerWins > hostWins) {
      resultDisplay = resultText.challengerWon;
    } else if (hostWins > challengerWins) {
      resultDisplay = resultText.hostWon;
    } else {
      resultDisplay = resultText.tie;
    }
  }

  return resultDisplay;
};

/**
 * Formats the MatchUpPhases to be shown on the ScheduleTab component.
 */
export const formatScheduleHistoryData = ({
  phases,
  hostId,
  resultText,
}: {
  phases: MatchUpPhaseFragment[];
  hostId: string;
  resultText: ResultText;
}) =>
  phases
    .map((phase): ScheduleTableData | null => {
      const match = phase.matches[0];

      if (!isTypename('PlayableMatchUpMatch', match)) {
        return null;
      }

      const participant = [
        ...getTeamParticipantsFromParticipantReference(
          match.rounds[0].participants
        ),
        ...getIndividualParticipantsFromParticipantReference(
          match.rounds[0].participants
        ),
      ].find((participant) => participant.id !== hostId);

      if (!participant) {
        return null;
      }

      return {
        participantId: participant.id,
        participantName:
          (isTypename('TeamParticipant', participant)
            ? participant.teamMembers.length === 1
              ? participant.teamMembers[0].person?.displayName
              : participant.team?.name
            : participant.person?.displayName) ?? '',
        participantAvatarUrl:
          (isTypename('TeamParticipant', participant)
            ? participant.teamMembers.length === 1
              ? participant.teamMembers[0].person?.avatarURL
              : participant.team?.avatarURL
            : participant.person?.avatarURL) ?? '',
        matchData: match,
        result: getResultText(
          match,
          getChallengerAndHostScoresFromMatch(match, participant.id, hostId),
          resultText
        ),
      };
    })
    // Narrow the nulls out
    .filter(isFilled)
    // Sort by start time
    .sort((a, b) => {
      if (a.matchData.startTime && !b.matchData.startTime) {
        return -1;
      }

      if (!a.matchData.startTime && b.matchData.startTime) {
        return 1;
      }

      if (a.matchData.startTime && b.matchData.startTime) {
        return (
          DateTime.fromISO(a.matchData.startTime, { zone: 'utc' })
            .toLocal()
            .toMillis() -
          DateTime.fromISO(b.matchData.startTime, { zone: 'utc' })
            .toLocal()
            .toMillis()
        );
      }

      return 0;
    });

const FAKE_UPCOMING_MATCH_ID = 'UPCOMING_MATCH';
/**
 * We show upcoming challengers using the `upcomingKOTHParticipant` field, but there
 * is no actual match object that exists yet for that participant, we just know they're up next.
 *
 * Rather than handle the display of their upcoming match differently from our already existing
 * display, we generate a fake match that can be handed to anything expecting to show a match.
 */
export const createFakeMatchUpFromUpcomingKOTHParticipant = (
  upcomingParticipant: UpcomingKingOfTheHillParticipantFragment,
  host: MatchUpTeamParticipantFragment | MatchUpIndividualParticipantFragment
): PlayableMatchUpMatchFragment => ({
  id: FAKE_UPCOMING_MATCH_ID,
  __typename: 'PlayableMatchUpMatch',
  gameSpecificDetails: null,
  isComplete: false,
  participants: [
    upcomingParticipant.participant,
    {
      participant: host,
      __typename: 'CompetitionParticipantReferenceWithRoster',
    },
  ],
  rounds: [
    {
      id: 'FAKE_ROUND_ID',
      isComplete: false,
      roundNumber: 0,
      __typename: 'MatchUpRound',
      participants: [
        {
          participant: upcomingParticipant.participant.participant,
          __typename: 'CompetitionParticipantReference',
        },
        {
          participant: host,
          __typename: 'CompetitionParticipantReference',
        },
      ],
    },
  ],
  startTime: upcomingParticipant.expectedStartTime,
});

export const isUpcomingMatch = (matchId: string) =>
  matchId === FAKE_UPCOMING_MATCH_ID;
